import { useState, useEffect, useCallback } from 'react';

// Applies the chosen theme to <html> and persists it. 'system' follows the OS.
const applyTheme = (selectedTheme) => {
  const root = window.document.documentElement;
  const isDark =
    selectedTheme === 'dark' ||
    (selectedTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  if (isDark) root.classList.add('dark');
  else root.classList.remove('dark');

  localStorage.setItem('app-theme', selectedTheme);
};

/**
 * useTheme — shared light/dark/system theme state.
 *
 * Reads the saved choice (default 'system') on mount, applies it, and keeps the
 * document in sync when the OS theme changes while on 'system'. Returns the
 * current theme, a setter, and the list of options for rendering a picker.
 */
export const THEME_OPTIONS = [
  { id: 'light', label: 'Light', icon: '☀️' },
  { id: 'dark', label: 'Dark', icon: '🌙' },
  { id: 'system', label: 'System', icon: '💻' },
];

export default function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('app-theme') || 'system');

  // Apply on mount and whenever the choice changes.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS changes while on 'system'.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => { if (theme === 'system') applyTheme('system'); };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const chooseTheme = useCallback((next) => setTheme(next), []);

  return { theme, setTheme: chooseTheme, options: THEME_OPTIONS };
}
